'use client'
import { useEffect } from 'react'
import Tracker from '@openreplay/tracker'
import trackerAssist from '@openreplay/tracker-assist'

const OpenReplaySnippet = () => {
  useEffect(() => {
    // Only initialize OpenReplay in production or when explicitly enabled
    if (typeof window === 'undefined') return

    const projectKey = process.env.NEXT_PUBLIC_OPENREPLAY_PROJECT_KEY

    if (!projectKey) {
      console.warn(
        'OpenReplay: Project key not found. Please set NEXT_PUBLIC_OPENREPLAY_PROJECT_KEY environment variable.'
      )
      return
    }

    // Check if recording should be enabled
    const shouldRecord = process.env.NODE_ENV === 'production' || process.env.NEXT_PUBLIC_OPENREPLAY_ENABLED === 'true'

    if (!shouldRecord) {
      console.log('OpenReplay: Recording disabled for current environment')
      return
    }

    try {
      const tracker = new Tracker({
        projectKey: projectKey,
        // Optional configuration
        __DISABLE_SECURE_MODE: true,

        // Privacy settings
        obscureTextEmails: true,
        obscureTextNumbers: false,
        obscureInputEmails: true,

        // Performance settings
        capturePerformance: true,
        captureResourceTimings: true,

        // Network settings
        network: {
          capturePayload: false, // Set to true if you want to capture request/response payloads
          sanitizer: (data: any) => {
            // Sanitize sensitive data
            if (data.url && data.url.includes('/api/payment')) {
              return null // Don't record payment API calls
            }
            return data
          },
          sessionTokenHeader: '',
          failuresOnly: false,
          ignoreHeaders: [],
          captureInIframes: false
        },

        // Console settings - handled automatically by OpenReplay

        // Exception settings
        captureExceptions: true
      })

      // Add TrackerAssist plugin for co-browsing
      tracker.use(trackerAssist({}))

      // Start the tracker
      tracker.start()

      // Optional: Set user information if available
      const setUserInfo = () => {
        try {
          // You can customize this based on your user authentication system
          const userId = sessionStorage.getItem('userId') || 'anonymous'
          const userEmail = sessionStorage.getItem('userEmail')

          tracker.setUserID(userId)

          if (userEmail) {
            tracker.setMetadata('email', userEmail)
          }

          // Add transaction info if available
          const transactionNo = sessionStorage.getItem('transactionNo')
          if (transactionNo) {
            tracker.setMetadata('transactionNo', transactionNo)
          }
        } catch (error) {
          console.warn('OpenReplay: Error setting user info:', error)
        }
      }

      // Set initial user info
      setUserInfo()

      // Listen for storage changes to update user info
      const handleStorageChange = () => {
        setUserInfo()
      }

      window.addEventListener('storage', handleStorageChange)
      window.addEventListener('transactionNoUpdated', handleStorageChange)

      // Cleanup
      return () => {
        window.removeEventListener('storage', handleStorageChange)
        window.removeEventListener('transactionNoUpdated', handleStorageChange)
        tracker.stop()
      }
    } catch (error) {
      console.error('OpenReplay initialization failed:', error)
    }
  }, [])

  return null // This component doesn't render anything
}

export default OpenReplaySnippet
